import React from "react";
import { Paper, Tabs, Tab } from "@mui/material";

/**
 * A reusable tab component with customizable properties
 * 
 * @param {Object} props - Component props
 * @param {number} props.value - Current selected tab value
 * @param {Function} props.onChange - Function called when tab changes
 * @param {Array} props.tabs - Array of tab objects with label, textSize, color, and weight properties
 * @param {boolean} props.isMobile - Whether the device is mobile or not
 * @param {Object} props.theme - Theme object
 * @param {Object} props.paperSx - Additional styles for Paper component
 * @param {Object} props.tabsSx - Additional styles for Tabs component
 * @param {Object} props.tabSx - Additional styles for individual Tab components
 */
const CTab = ({
  value,
  onChange,
  tabs,
  isMobile,
  theme,
  paperSx = {},
  tabsSx = {},
  tabSx = {},
}) => {
  // Create a wrapper function to ensure we pass the correct arguments to onChange
  const handleChange = (event, newValue) => {
    if (onChange) {
      onChange(event, newValue);
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 4,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        overflow: "hidden",
        ...paperSx,
      }}
    >
      <Tabs
        value={value}
        onChange={handleChange}
        variant={isMobile ? "standard" : "fullWidth"}
        scrollButtons={false}
        textColor="inherit"
        sx={{
          "& .MuiTab-root": { py: 2 },
          bgcolor: theme.palette.background.paper,
          ...(isMobile && {
            "& .MuiTabs-flexContainer": {
              flexWrap: "wrap",
              justifyContent: "flex-start",
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
            "& .Mui-selected": {
              backgroundColor: theme.palette.action.selected,
            },
          }),
          ...tabsSx,
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            label={tab.label}
            sx={{
              fontWeight: tab.weight || 700,
              fontSize: tab.textSize || "inherit",
              color: tab.color || "inherit",
              ...(isMobile && {
                flexGrow: 0,
                minWidth: "auto",
                margin: 0.5,
              }),
              ...tabSx,
            }}
          />
        ))}
      </Tabs>
    </Paper>
  );
};

export default CTab;